import React, { forwardRef, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Box, ButtonBase, Dialog, Grow, List, ListItemButton, Tooltip,
    ListItemIcon, ListItemText, TextField, InputAdornment, IconButton, Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { TransitionProps } from "@mui/material/transitions";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SearchIcon from "@mui/icons-material/Search";
import { useThread } from "../../Contexts/ThreadContext";
import { useLang } from "../../Contexts/LanguageContext";
import { getResourcesList, ResourceItem } from "../../APIs/resources";

// Smooth easing reused by the chip transition.
const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

// Scale-in "bubble pop" transition for the centered picker dialog.
const BubbleTransition = forwardRef(function BubbleTransition(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>,
) {
    return <Grow ref={ref} timeout={300} {...props} />;
});

// The report picker + "focused on" chip, rendered just below the chat input.
// The picker opens as a centered bubble dialog that pops in mid-screen. The
// selected report is shared with the rest of the app through ThreadContext.
const SourceSelector = () => {
    const { t, isRTL } = useLang();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const { sourceId, setSourceId, sourceTitle, setSourceTitle } = useThread();
    const [searchParams, setSearchParams] = useSearchParams();

    const [resources, setResources] = useState<ResourceItem[]>([]);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickerQuery, setPickerQuery] = useState('');

    useEffect(() => {
        getResourcesList()
            .then(setResources)
            .catch(() => setResources([]));
    }, []);

    const resourceLabel = (r: ResourceItem) =>
        ((isRTL ? (r.title_ar || r.title_en) : (r.title_en || r.title_ar)) || r.resource_id) as string;

    const filteredResources = resources.filter(r =>
        resourceLabel(r).toLowerCase().includes(pickerQuery.trim().toLowerCase())
    );

    const closePicker = () => { setPickerOpen(false); setPickerQuery(''); };

    const selectResource = (r: ResourceItem | null) => {
        console.log("[select] resource_id=", r ? r.resource_id : null, "title=", r ? (r.title_en || r.title_ar) : null);
        setSourceId(r ? r.resource_id : null);
        setSourceTitle(r ? (r.title_en || r.title_ar || null) : null);
        closePicker();
    };

    // Deep-link preselection: open the chatbot scoped to a report via ?source_id=...
    // Applied once the resource list has loaded, then the param is stripped so it
    // doesn't re-apply if the user later clears the source from the UI.
    const preselectSourceId = searchParams.get("source_id");
    useEffect(() => {
        if (!preselectSourceId || resources.length === 0) return;
        const match = resources.find(r => r.resource_id === preselectSourceId);
        console.log("[preselect] param=", preselectSourceId, "resources=", resources.length,
            "match=", match, "ids=", resources.map(r => r.resource_id));
        if (match) selectResource(match);
        searchParams.delete("source_id");
        setSearchParams(searchParams, { replace: true });
    }, [preselectSourceId, resources]);

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, alignSelf: 'center' }}>
            <Tooltip title={sourceTitle ? `${t.focusedOn}: ${sourceTitle}` : t.sourcePlaceholder}>
                <ButtonBase
                    onClick={() => setPickerOpen(true)}
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        fontSize: '11px',
                        fontWeight: 600,
                        lineHeight: 1,
                        whiteSpace: 'nowrap',
                        padding: '5px 9px',
                        borderRadius: '8px',
                        border: '1px solid',
                        transition: `background-color 200ms ${EASE}, color 200ms ${EASE}`,
                        fontFamily: isRTL ? "'Noto Kufi Arabic', sans-serif" : 'inherit',
                        // Active (report focused) → navy tint; idle → subtle chip that
                        // adapts to light/dark so it never glares inside the input pill.
                        color: sourceId ? '#00314f' : (isDark ? 'rgba(255,255,255,0.75)' : '#4a5c68'),
                        borderColor: sourceId ? 'rgba(0,49,79,0.20)' : (isDark ? 'rgba(255,255,255,0.14)' : 'rgba(18,48,63,0.08)'),
                        backgroundColor: sourceId ? 'rgba(0,49,79,0.12)' : (isDark ? 'rgba(255,255,255,0.06)' : '#eef2f4'),
                        '&:hover': { backgroundColor: sourceId ? 'rgba(0,49,79,0.16)' : (isDark ? 'rgba(255,255,255,0.10)' : '#e4ebf0') },
                    }}
                >
                    <DescriptionOutlinedIcon sx={{ fontSize: 15, color: sourceId ? '#00314f' : 'inherit' }} />
                    <span>{t.sourceChip}</span>
                </ButtonBase>
            </Tooltip>

            <Dialog
                open={pickerOpen}
                onClose={closePicker}
                TransitionComponent={BubbleTransition}
                slotProps={{
                    paper: {
                        dir: isRTL ? 'rtl' : 'ltr',
                        sx: {
                            // Grow to fit report titles, but never wider than the cap
                            // (or the viewport on small screens).
                            width: 'fit-content',
                            minWidth: 'min(420px, 90vw)',
                            maxWidth: 'min(720px, 92vw)',
                            borderRadius: '24px',
                            overflow: 'hidden',
                            boxShadow: '0 24px 60px rgba(0,49,79,0.28)',
                        },
                    },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px 6px' }}>
                    <Typography variant="subtitle1" sx={{ color: '#00314f', fontWeight: 700, fontFamily: isRTL ? "'Noto Kufi Arabic', sans-serif" : 'inherit' }}>
                        {t.source}
                    </Typography>
                    <IconButton size="small" onClick={closePicker}>
                        <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                </Box>
                <Box sx={{ padding: '0 18px 18px' }}>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        placeholder={t.sourcePlaceholder}
                        value={pickerQuery}
                        onChange={(e) => setPickerQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ marginBottom: 1 }}
                    />
                    <List dense sx={{ maxHeight: 320, overflowY: 'auto', padding: 0 }}>
                        {sourceId && (
                            <ListItemButton onClick={() => selectResource(null)}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <CloseRoundedIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={t.clearSource} />
                            </ListItemButton>
                        )}
                        {filteredResources.length === 0 ? (
                            <Typography variant="body2" sx={{ color: 'text.secondary', padding: 2, textAlign: 'center' }}>
                                {t.noReports}
                            </Typography>
                        ) : (
                            filteredResources.map((r) => (
                                <ListItemButton
                                    key={r.resource_id}
                                    selected={r.resource_id === sourceId}
                                    onClick={() => selectResource(r)}
                                >
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <DescriptionRoundedIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={resourceLabel(r)}
                                        primaryTypographyProps={{ noWrap: true }}
                                    />
                                </ListItemButton>
                            ))
                        )}
                    </List>
                </Box>
            </Dialog>
        </Box>
    );
};

export default SourceSelector;
